import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Users, CalendarDays, MailCheck, ChevronRight, UserPlus, RefreshCw, CalendarClock, ChevronDown, Archive } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

const EMPTY_IMG =
  "https://images.unsplash.com/photo-1761546475596-f38c958e7a37?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMHdhcm0lMjBvcmdhbmljJTIwc2hhcGVzJTIwYmVpZ2UlMjBjYWxtfGVufDB8fHx8MTc4Mjg2Njg5N3ww&ixlib=rb-4.1.0&q=85";

const StatCard = ({ icon: Icon, label, value, tint }) => (
  <div
    className="bg-white rounded-2xl border border-[#EAE6DF] p-6 shadow-[0_4px_20px_rgba(45,44,40,0.04)]"
    data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}
  >
    <div className="flex items-center justify-between">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${tint}`}>
        <Icon className="w-5 h-5" strokeWidth={1.5} />
      </div>
    </div>
    <p className="font-heading text-3xl font-bold text-[#2D2C28] mt-4">{value}</p>
    <p className="text-sm text-[#757169] mt-1">{label}</p>
  </div>
);

const PatientCard = ({ patient: p, navigate, dimmed }) => (
  <button
    onClick={() => navigate(`/patients/${p.id}`)}
    data-testid={`patient-card-${p.id}`}
    className={`group text-left bg-white rounded-2xl border border-[#EAE6DF] p-5 shadow-[0_4px_20px_rgba(45,44,40,0.04)] hover:border-[#C07761]/40 hover:shadow-[0_6px_24px_rgba(192,119,97,0.10)] transition-all ${dimmed ? "opacity-60 hover:opacity-100" : ""}`}
  >
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 rounded-full bg-[#7B968B]/15 text-[#7B968B] flex items-center justify-center font-heading font-semibold text-lg shrink-0">
        {p.name.charAt(0).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-heading font-medium text-[#2D2C28] truncate">{p.name}</p>
        <p className="text-sm text-[#757169] truncate">
          {p.last_session_at
            ? `Dernière séance : ${format(parseISO(p.last_session_at), "d MMM yyyy", { locale: fr })}`
            : "Aucune séance"}
        </p>
      </div>
      <ChevronRight className="w-5 h-5 text-[#C0BBB0] group-hover:text-[#C07761] group-hover:translate-x-0.5 transition-all" />
    </div>
  </button>
);

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [stats, setStats] = useState({ patients: 0, sessions: 0, emails_sent: 0 });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", language: "fr", notes: "" });
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const load = async () => {
    try {
      const [p, s, a] = await Promise.all([api.get("/patients"), api.get("/stats"), api.get("/appointments")]);
      setPatients(p.data);
      setStats(s.data);
      setAppointments(a.data);
    } catch (e) {
      toast.error("Impossible de charger les données");
    } finally {
      setLoading(false);
    }
  };

  const syncCalendly = async () => {
    setSyncing(true);
    try {
      const res = await api.post("/calendly/sync");
      const d = res.data;
      toast.success(
        `Calendly synchronisé : ${d.patients_created} patient(s) créé(s), ${d.patients_updated} mis à jour, ${d.sessions_created} séance(s) importée(s), ${d.appointments} RDV à venir.`
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de la synchronisation Calendly");
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const createPatient = async () => {
    if (!form.name.trim()) {
      toast.error("Le nom du patient est requis");
      return;
    }
    setSaving(true);
    try {
      const res = await api.post("/patients", form);
      toast.success("Patient ajouté");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", language: "fr", notes: "" });
      navigate(`/patients/${res.data.id}`);
    } catch (e) {
      toast.error("Erreur lors de l'ajout");
    } finally {
      setSaving(false);
    }
  };

  const firstName = (() => {
    const n = user?.name?.trim() || "";
    const parts = n.split(/\s+/).filter((p) => !/^(dr|dre|m|mme|mr|mrs|ms)\.?$/i.test(p));
    return (parts[0] || n).replace(/[.,]$/, "");
  })();

  const activePatients = patients.filter((p) => p.active !== false);
  const inactivePatients = patients.filter((p) => p.active === false);

  return (
    <AppLayout>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-[#C07761]">Tableau de bord</p>
          <h1 className="font-heading text-4xl font-semibold text-[#2D2C28] tracking-tight mt-1">
            Bonjour, {firstName} 👋
          </h1>
          <p className="text-[#757169] mt-2">
            Retrouvez vos patients et préparez vos e-mails de suivi.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            onClick={syncCalendly}
            disabled={syncing}
            data-testid="sync-calendly-btn"
            className="rounded-xl border-[#EAE6DF] text-[#575349] h-11 px-5"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Synchronisation…" : "Synchroniser Calendly"}
          </Button>
          <AddPatientDialog
            open={open}
            setOpen={setOpen}
            form={form}
            setForm={setForm}
            saving={saving}
            onSave={createPatient}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-8">
        <StatCard icon={Users} label="Patients" value={stats.patients} tint="bg-[#C07761]/10 text-[#C07761]" />
        <StatCard icon={CalendarDays} label="Séances" value={stats.sessions} tint="bg-[#7B968B]/15 text-[#7B968B]" />
        <StatCard icon={MailCheck} label="E-mails envoyés" value={stats.emails_sent} tint="bg-[#D4A373]/15 text-[#B7854f]" />
      </div>

      {appointments.length > 0 && (
        <div className="mt-10" data-testid="upcoming-appointments-section">
          <h2 className="font-heading text-xl font-medium text-[#2D2C28] mb-4 flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-[#7B968B]" strokeWidth={1.5} /> Rendez-vous à venir
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {appointments.slice(0, 6).map((a) => (
              <button
                key={a.calendly_event_uri + a.patient_id}
                onClick={() => navigate(`/patients/${a.patient_id}`)}
                data-testid={`appointment-card-${a.patient_id}`}
                className="text-left bg-white rounded-2xl border border-[#EAE6DF] p-4 shadow-[0_4px_20px_rgba(45,44,40,0.04)] hover:border-[#7B968B]/50 transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#7B968B]/15 text-[#7B968B] flex items-center justify-center shrink-0">
                    <CalendarClock className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-heading font-medium text-[#2D2C28] truncate">{a.patient_name}</p>
                    <p className="text-sm text-[#757169] truncate">
                      {format(parseISO(a.start_time), "EEEE d MMM 'à' HH:mm", { locale: fr })} · {a.event_name}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-10">
        <h2 className="font-heading text-xl font-medium text-[#2D2C28] mb-4">Vos patients</h2>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 bg-white/60 rounded-2xl border border-[#EAE6DF] animate-pulse" />
            ))}
          </div>
        ) : patients.length === 0 ? (
          <div className="bg-white rounded-2xl border border-[#EAE6DF] p-10 text-center shadow-[0_4px_20px_rgba(45,44,40,0.04)]">
            <img src={EMPTY_IMG} alt="" className="w-24 h-24 object-cover rounded-2xl mx-auto mb-5 opacity-90" />
            <h3 className="font-heading text-lg font-medium text-[#2D2C28]">Aucun patient pour le moment</h3>
            <p className="text-[#757169] mt-1 max-w-sm mx-auto">
              Ajoutez votre premier patient pour commencer à générer des e-mails de suivi.
            </p>
            <Button
              onClick={() => setOpen(true)}
              data-testid="empty-add-patient-btn"
              className="mt-5 rounded-xl bg-[#C07761] hover:bg-[#A66350] text-white"
            >
              <UserPlus className="w-4 h-4 mr-2" /> Ajouter un patient
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {activePatients.map((p) => (
              <PatientCard key={p.id} patient={p} navigate={navigate} />
            ))}
          </div>
        )}

        {inactivePatients.length > 0 && (
          <div className="mt-8" data-testid="inactive-patients-section">
            <button
              onClick={() => setShowInactive((v) => !v)}
              data-testid="toggle-inactive-patients-btn"
              className="flex items-center gap-2 text-sm font-medium text-[#757169] hover:text-[#2D2C28] transition-colors"
            >
              <Archive className="w-4 h-4" strokeWidth={1.5} />
              Patients inactifs ({inactivePatients.length})
              <ChevronDown className={`w-4 h-4 transition-transform ${showInactive ? "rotate-180" : ""}`} />
            </button>
            {showInactive && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                {inactivePatients.map((p) => (
                  <PatientCard key={p.id} patient={p} navigate={navigate} dimmed />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

const AddPatientDialog = ({ open, setOpen, form, setForm, saving, onSave }) => (
  <Dialog open={open} onOpenChange={setOpen}>
    <DialogTrigger asChild>
      <Button
        data-testid="add-patient-btn"
        className="rounded-xl bg-[#C07761] hover:bg-[#A66350] text-white h-11 px-5"
      >
        <Plus className="w-4 h-4 mr-2" /> Nouveau patient
      </Button>
    </DialogTrigger>
    <DialogContent className="rounded-2xl bg-[#FBF9F6]">
      <DialogHeader>
        <DialogTitle className="font-heading text-[#2D2C28]">Ajouter un patient</DialogTitle>
        <DialogDescription className="text-[#757169]">
          Renseignez les informations du patient. L'e-mail sera nécessaire pour envoyer les suivis.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-2">
        <div>
          <Label className="text-[#575349]">Nom complet *</Label>
          <Input
            data-testid="patient-name-input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Ex. Marie Dupont"
            className="mt-1.5 bg-white rounded-xl"
          />
        </div>
        <div>
          <Label className="text-[#575349]">Adresse e-mail</Label>
          <Input
            data-testid="patient-email-input"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="marie.dupont@email.com"
            className="mt-1.5 bg-white rounded-xl"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[#575349]">Téléphone</Label>
            <Input
              data-testid="patient-phone-input"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="06 12 34 56 78"
              className="mt-1.5 bg-white rounded-xl"
            />
          </div>
          <div>
            <Label className="text-[#575349]">Langue</Label>
            <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
              <SelectTrigger data-testid="patient-language-select" className="mt-1.5 bg-white rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fr">🇫🇷 Français</SelectItem>
                <SelectItem value="en">🇬🇧 English</SelectItem>
                <SelectItem value="es">🇪🇸 Español</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-[#575349]">Notes (optionnel)</Label>
          <Textarea
            data-testid="patient-notes-input"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Contexte, objectifs thérapeutiques…"
            className="mt-1.5 bg-white rounded-xl min-h-[80px]"
          />
        </div>
      </div>
      <DialogFooter>
        <Button
          onClick={onSave}
          disabled={saving}
          data-testid="save-patient-btn"
          className="rounded-xl bg-[#C07761] hover:bg-[#A66350] text-white w-full"
        >
          {saving ? "Enregistrement…" : "Ajouter le patient"}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default Dashboard;
